<template>
  <div id="contents" class="bg-contents py-15">
    <v-container>
      <div class="contents-grid">
        <h2 class="heading text-center mb-12">{{ $t("contents.heading") }}</h2>
        <!-- <p class="description text-body-2 text-left mb-12"
        v-html="$t('contents.description')"
        ></p> -->
        <div class="grid-container">
          <!-- カード表示 -->
          <div
            v-for="(item, index) in localizedItems"
            :key="index"
            class="content-card"
            @click="openModal(item)"
            :class="{
              large: isLargeCard(index) && !isMobile,
              medium: isMediumCard(index) && !isMobile,
              small: isSmallCard(index) && !isMobile,
            }"
          >
            <v-img :src="item.fullImagePath" cover class="content-image">
              <div class="title-overlay"></div>
              <p class="card-title text-caption text-sm-body-2">
                <span v-html="item.title"></span>
              </p>
            </v-img>
          </div>
        </div>
        <!-- モーダル表示 -->
        <v-dialog v-model="isModalOpen" class="modal-card">
      <v-btn
          @click="closeModal"
            class="close-btn"
            icon="$close"
            size="large"
            variant="text"
          ></v-btn>

          <v-card>
            <v-img :src="selectedCard?.fullImagePath" cover></v-img>
            <v-card-item>
              <p v-if="selectedCard?.label" class="modal-label">
                {{ selectedCard.label }}
              </p>
              <p v-if="selectedCard?.subtitle" class="modal-subtitle">
                {{ selectedCard.subtitle }}
              </p>
              <h3 class="modal-title">
                {{ selectedCard?.title }}
              </h3>
              <p class="text-body-2 text-sm-body-1 mt-2 text-pre-wrap">
                <span v-html="selectedCard?.description"></span>
              </p>
              <p v-if="selectedCard?.price" class="text-body-2 text-sm-body-1 mt-2 text-pre-wrap text-right font-weight-bold">
                <span v-html="selectedCard?.price"></span>
              </p>
              <p v-if="selectedCard?.note" class="text-body-2 text-sm-body-1 mt-2 text-pre-wrap text-note">
                <span v-html="selectedCard?.note"></span>
              </p>
            </v-card-item>
          </v-card>
        </v-dialog>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import localeData, { setOrder, imagePathsMapping } from "@/locales/ContentsMain";
import { getBasePath } from '@/utils/basePath';

// 翻訳データの設定
const { t, locale } = useI18n();
const localizedItems = ref([]);
const isModalOpen = ref(false);
const selectedCard = ref(null);

// basePathユーティリティからベースパスを取得
const basePath = computed(() => getBasePath());

const updateLocalizedItems = () => {
  const data = localeData[locale.value]?.contents.items || [];
  const set1 = setOrder['set-1'];
  
  // set-1のcardsの順序に基づいてアイテムをソート
  const sortedItems = set1.cards.map(cardConfig => {
    const item = data.find(item => item.id === cardConfig.id);
    return item ? {
      ...item,
      fullImagePath: `${basePath.value}${imagePathsMapping[item.id]}`,
    } : null;
  }).filter(item => item && item.visible);
  
  localizedItems.value = sortedItems;
};

const openModal = (item) => {
  selectedCard.value = item;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedCard.value = null;
};

// スマホ判定
const isMobile = window.innerWidth <= 767;

// セットベースのlargeIndicesを使用
const largeIndices = setOrder['set-1'].largeIndices;

// カードのクラスを判定する関数
const isLargeCard = (index) => largeIndices.includes(index);
const isMediumCard = (index) => !isLargeCard(index) && index % 3 === 0;
const isSmallCard = (index) => !isLargeCard(index) && !isMediumCard(index);

// 初期化と監視
updateLocalizedItems();
watch(locale, updateLocalizedItems);
</script>

<style scoped lang="scss">
.contents-grid {
  padding: 16px;
}

.grid-container {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.content-card {
  position: relative;
  cursor: pointer;
  overflow: hidden;
    aspect-ratio: 1 / 1; /* 正方形を維持 */
  @media screen and (max-width: 767px) {
    width: 100%;
  }
}

.content-image {
  width: 100%;
  height: 100%;
}

.large {
  grid-column: span 2;
  grid-row: span 2;
}

.medium {
  grid-column: span 1;
  grid-row: span 1;
}

.small {
  grid-column: span 1;
  grid-row: span 1;
}

.title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3em;
  mix-blend-mode: multiply;
  z-index: 1;
  &::before {
    position: absolute;
    content: "";
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      0deg,
      rgba(var(--v-theme-primary), 0.9) 0%,
      rgba(var(--v-theme-primary), 0.5) 70%,
      rgba(141, 141, 141, 0)
    );
    z-index: 2;
  }
}



.card-title {
  position: absolute;
  color: white;
  bottom: 0;
  left: 0;
  margin: 4px 8px;
  line-height: 1.2;
  font-weight: 700;
  z-index: 3;
  overflow: hidden;
  display: -webkit-box; /* 必要: 行数制限のための表示方式 */
  -webkit-box-orient: vertical; /* 必要: 行の向きを指定 */
  -webkit-line-clamp: 4; /* 行数を制限 */
  overflow: hidden; /* 必要: 余分な行を隠す */
  text-overflow: ellipsis; /* 必要: 「…」を表示 */
}


.modal-card {
  max-width: 600px;
  @media screen and (min-width: 1280px) {
    max-width: 800px;
  }
}


.v-card {
  background-color: rgba(var(--v-theme-background), 1);
}

.v-card-title {
    white-space: wrap;
}

.close-btn {
  position: absolute;
  right: 0;
  top: -50px;
  font-size: 20px;
  color: white;
}

.modal-label {
  background: rgb(var(--v-theme-primary), 1);
  font-weight: bold;
  display: inline-block;
  padding: 0.2em 0.3em 0.2em;
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  padding: 0;
  list-style: none;
  &::before {
    content: none;
  }
}

.modal-subtitle {
  color: white;
  font-size: 12px;
  margin: 0;
  padding: 0;
}

.bg-contents {
  background-color: rgba(var(--v-theme-primary), 0.3);
}

/* モーダルの背景 */
:deep(.v-overlay__scrim) {
  background: rgb(var(--v-theme-note), 1);
  opacity: 0.7;
}
/* モーダルの高さ設定 */
:deep(.v-overlay__content) {
  max-height: calc(100% - 100px)!important; //上下50pxずつ余白を取る
}
</style>
